import type { PaymentDetails } from '@/shared/api/types'
import { useCopy } from '@/shared/lib/hooks/useCopy'
import { coinOf, networkOf } from '@/shared/lib/currencies'
import { crypto } from '@/shared/lib/format'
import { CheckIcon, CopyIcon, QrIcon } from '@/shared/ui/icons'
import styles from './Payment.module.css'

interface Props {
  payment: PaymentDetails
  invoiceId: string
  onShowQr: () => void
  /**
   * Сколько осталось доплатить. Задаётся только при недоплате.
   *
   * Без него экран доплаты показывал бы ПОЛНУЮ сумму заявки: человек
   * читает «не хватает 0.3», прокручивает вниз, копирует «1.0»
   * и отправляет ещё раз всю сумму. Заявка закрывается переплатой,
   * а разницу продавец возвращает вручную — если вообще заметит.
   */
  remaining?: string | null
}

/**
 * Реквизиты перевода: сумма, адрес, номер заявки.
 *
 * Сумма выводится ПОЛНОСТЬЮ, до последнего знака. В макете здесь стоит
 * «193 USDT», но настоящая сумма — «193.048231», и округлённая означает,
 * что плательщик отправит ровно 193 и недоплатит. Допуск по недоплате
 * нулевой: деньги мерчанту лягут, заказ не закроется.
 */
export function Details({ payment, invoiceId, onShowQr, remaining }: Props) {
  const { copied, copy } = useCopy()
  const coin = coinOf(payment.currency)
  const network = networkOf(payment.currency)
  const topUp = remaining !== undefined && remaining !== null
  /*
    `crypto()` срезает ХВОСТОВЫЕ НУЛИ и ничего не округляет.
    Бекенд отдаёт Decimal во всю ширину колонки — «0.001846160000000000».
    Восемнадцать знаков плательщик переносит в кошелёк с ошибкой чаще,
    чем восемь: глаз теряет позицию в череде нулей. Округлять при этом
    нельзя ни в какую сторону — вниз это недоплата и зависшая заявка.
  */
  const amount = crypto(topUp ? remaining : payment.crypto_amount)

  return (
    <div className={styles.details}>
      <div className={styles.row}>
        <div className={styles.rowHead}>
          <span className={styles.rowLabel}>
            {topUp ? 'Осталось доплатить' : 'Сумма к оплате'}
          </span>
          <Act done={copied === 'amount'} label="Скопировать сумму"
               onClick={() => void copy(amount, 'amount')} />
        </div>
        <span className={styles.rowValue}>
          {amount} {coin.ticker} • {network}
        </span>
      </div>

      <div className={styles.row}>
        <div className={styles.rowHead}>
          <span className={styles.rowLabel}>Адрес получателя</span>
          <Act done={copied === 'address'} label="Скопировать адрес"
               onClick={() => void copy(payment.address, 'address')} />
          <button type="button" className={styles.act} onClick={onShowQr}
                  aria-label="Показать QR-код">
            <QrIcon />
          </button>
        </div>
        <span className={styles.rowValue}>{payment.address}</span>
      </div>

      <div className={styles.row}>
        <div className={styles.rowHead}>
          <span className={styles.rowLabel}>Номер заявки</span>
          <Act done={copied === 'id'} label="Скопировать номер заявки"
               onClick={() => void copy(invoiceId, 'id')} />
        </div>
        <span className={styles.rowValue}>{invoiceId}</span>
      </div>
    </div>
  )
}

function Act({ done, label, onClick }: { done: boolean; label: string; onClick: () => void }) {
  return (
    <button type="button" className={styles.act} data-done={done} onClick={onClick}
            aria-label={done ? 'Скопировано' : label}>
      {done ? <CheckIcon /> : <CopyIcon />}
    </button>
  )
}
